import React from "react";

import {
	faAddressCard,
	faBriefcase,
	faBuilding,
	faChevronDown,
	faChevronUp,
	faCrown,
	faExternalLink,
	faLayerGroup,
	faUserCircle,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import IconButton from "~/ui/button/IconButton";

import FieldRow from "./FieldRow";
import type { SalesforceAiSuggestionsFieldDictType } from "./SalesforcePanel";

import type { SalesforceQuery$data } from "~/__generated__/SalesforceQuery.graphql";

import styles from "./ObjectCard.module.css";

export type SalesforceObjectRecordType = NonNullable<SalesforceQuery$data["salesforceContactsForStream"]>[number];

function getObjectLogo(type: string): IconDefinition {
	switch (type) {
		case "Contact":
			return faAddressCard;
		case "Account":
			return faBuilding;
		case "Opportunity":
			return faCrown;
		case "Case":
			return faBriefcase;
		case "Lead":
			return faUserCircle;
		default:
			return faLayerGroup;
	}
}

function getObjectLogoColor(type: string) {
	switch (type) {
		case "Contact":
			return styles.contact;
		case "Account":
			return styles.account;
		case "Opportunity":
			return styles.opportunity;
		case "Case":
			return styles.case;
		case "Lead":
			return styles.lead;
		default:
			return undefined;
	}
}

function ObjectPill({ name, label }: { name: string; label: string }) {
	const logo = getObjectLogo(name);
	const logoColor = getObjectLogoColor(name);

	return (
		<div className={cx(styles.objectPill, logoColor)}>
			<FontAwesomeIcon fixedWidth icon={logo} />
			<p className={styles.label}>{label}</p>
		</div>
	);
}

type ObjectCardProps = Omit<SalesforceObjectRecordType, "references" | "__typename"> & {
	canOpenDetails: boolean;
	handleRecordChange: (
		objectName: string,
		recordId: string,
		fieldName: string,
		originalValue: string,
		newValue: string,
	) => void;
	undoTrigger: number;
	aiSuggestions?: SalesforceAiSuggestionsFieldDictType;
	isOpen?: boolean;
	forceViewOnly?: boolean;
};

export default function ObjectCard({
	name,
	id,
	label,
	value,
	url,
	fields,
	canOpenDetails,
	handleRecordChange,
	undoTrigger,
	aiSuggestions,
	isOpen: controlledIsOpen,
	forceViewOnly,
}: ObjectCardProps) {
	const defaultIsOpen = canOpenDetails && (name === "Contact" || name === "Account");
	const [isOpen, setIsOpen] = React.useState<boolean>(defaultIsOpen);
	const toggleOpen = React.useCallback(() => setIsOpen(isOpen => !isOpen), []);

	const hasFields = fields.length > 0;

	const handleFieldChange = React.useCallback(
		(fieldName: string, originalValue: string, newValue: string) => {
			handleRecordChange(name, id, fieldName, originalValue, newValue);
		},
		[handleRecordChange, name, id],
	);

	React.useEffect(() => {
		if (controlledIsOpen !== undefined) {
			setIsOpen(controlledIsOpen);
		}
	}, [controlledIsOpen]);

	return (
		<section className={styles.object}>
			<header className={styles.header}>
				<ObjectPill name={name} label={label} />
				<div className={styles.textContainer}>
					<a className={styles.name} title={value ?? ""} href={url} target="_blank" rel="noopener noreferrer">
						{value}
						<FontAwesomeIcon fixedWidth className={styles.externalLinkIcon} icon={faExternalLink} />
					</a>
					{canOpenDetails && (
						<IconButton
							variant="tertiary"
							size="small"
							title={isOpen ? "Close" : "Open"}
							icon={isOpen ? faChevronUp : faChevronDown}
							onClick={toggleOpen}
							className={styles.collapsableIcon}
						/>
					)}
				</div>
			</header>

			{hasFields ? (
				<div className={cx(styles.fieldsContainer, { [styles.isOpen]: isOpen })}>
					{fields.map(({ name, label, value: fieldValue, type, interactionType, length }) => (
						<FieldRow
							key={value + name}
							name={name}
							label={label}
							value={fieldValue}
							type={type}
							interactionType={forceViewOnly ? "VIEW_ONLY" : interactionType}
							handleFieldChange={handleFieldChange}
							undoTrigger={undoTrigger}
							aiSuggestion={aiSuggestions?.[label]}
							length={length}
						/>
					))}
				</div>
			) : (
				<div className={cx(styles.noFields, { [styles.isOpen]: isOpen })}>
					<p>An admin can configure the fields to be displayed here in your Workspace integration settings</p>
				</div>
			)}
		</section>
	);
}
